import { Typography, Button, Input, Label } from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import { cn } from "@/lib/utils";
import { useMemo } from "react";
import StepCard from "../../CompletedStepCard";
import FilterRow from "./FilterRow";
import {
  Control,
  Controller,
  FieldErrors,
  useFieldArray,
  useWatch,
  UseFormSetValue,
} from "react-hook-form";
import { SubscriptionFormData } from "@/pages/subscriptions/services/types";
import type { SubscriptionMetricsResponse } from "@/pages/subscriptions/services/types";
import type { GroupedBySource } from "../../types";
import { useFormFieldReset } from "./customHooks/useFormFieldReset";
import SubscriptionTypeSelector from "./SubscriptionTypeSelector";
import EntityDetailsCard from "./EntityDetailsCard";
import { SubscriptionType, REPORT_RESTRICTIONS } from "@/pages/subscriptions/constants";

interface SubscriptionTypeStepProps {
  control: Control<SubscriptionFormData>;
  errors: FieldErrors<SubscriptionFormData>;
  setValue: UseFormSetValue<SubscriptionFormData>;
  onNext: () => void;
  metricsData?: SubscriptionMetricsResponse;
  groupedBySource: GroupedBySource;
  categoryDisplayNameLookup: Record<string, string>;
  categoryToSourceLookup: Record<string, string>;
  isEditMode?: boolean;
  isFromTemplate?: boolean;
}

const getReportRestrictionsForSource = (sourceKey?: string) => {
  if (sourceKey === "databricks") {
    return REPORT_RESTRICTIONS.BY_SOURCE.databricks;
  }
  return REPORT_RESTRICTIONS.BY_SOURCE.snowflake;
};

const SubscriptionTypeStep = ({
  control,
  errors,
  setValue,
  onNext,
  metricsData,
  groupedBySource,
  categoryDisplayNameLookup,
  categoryToSourceLookup,
  isEditMode = false,
  isFromTemplate = false,
}: SubscriptionTypeStepProps) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: "filters",
  });

  // Watch for changes in required fields
  const name = useWatch({ control, name: "name" });
  const subscriptionType = useWatch({ control, name: "rule_type" });
  const source = useWatch({ control, name: "source" });
  const sourceName = useWatch({ control, name: "source_name" });
  const metric = useWatch({ control, name: "metric" });
  const filters = useWatch({ control, name: "filters" });

  // Check if all required fields are filled
  const isFormValid = Boolean(name && name.trim() !== "" && subscriptionType && source && metric);

  const isReport = subscriptionType === SubscriptionType.REPORT;

  // For reports, restrict each source to the small set of report-backed
  // categories exposed by the backend executor for that platform.
  const effectiveGroupedBySource = useMemo(() => {
    if (!isReport) return groupedBySource;
    const restricted: GroupedBySource = {};
    for (const [sourceKey, sourceData] of Object.entries(groupedBySource)) {
      const restrictions = getReportRestrictionsForSource(sourceKey);
      const filteredCategories = Object.fromEntries(
        Object.entries(sourceData.categories).filter(([cat]) =>
          (restrictions.ALLOWED_CATEGORIES as readonly string[]).includes(cat)
        )
      );
      if (Object.keys(filteredCategories).length > 0) {
        restricted[sourceKey] = { ...sourceData, categories: filteredCategories };
      }
    }
    return restricted;
  }, [isReport, groupedBySource]);

  // Get metrics for selected category (restricted for reports)
  const metricsForCategory = useMemo(() => {
    if (!source || !metricsData) return [];
    const metrics = metricsData.filter(
      m => m.category === source && (!sourceName || m.source_name === sourceName)
    );
    if (!isReport) return metrics;
    return metrics.filter(m =>
      (getReportRestrictionsForSource(m.source_name).ALLOWED_METRICS as readonly string[]).includes(
        m.name
      )
    );
  }, [source, sourceName, metricsData, isReport]);

  // Get all supported filters for selected metric (restricted for reports)
  const allSupportedFilters = useMemo(() => {
    if (!metric || !metricsData) return [];
    const selectedMetric = metricsData.find(m => m.name === metric);
    const filters = selectedMetric?.supported_filters || [];
    if (!isReport) return filters;
    const restrictions = getReportRestrictionsForSource(selectedMetric?.source_name);
    return filters.filter(f => (restrictions.ALLOWED_FILTERS as readonly string[]).includes(f));
  }, [metric, metricsData, isReport]);

  // Get searchable filter keys for the selected metric
  const searchableFilters = useMemo(() => {
    if (!metric || !metricsData) return [];
    const selectedMetric = metricsData.find(m => m.name === metric);
    return selectedMetric?.searchable_filters ?? [];
  }, [metric, metricsData]);

  // Get dimensions that are already used by filters
  const usedDimensions = useMemo(() => {
    if (!filters) return new Set<string>();
    return new Set(filters.map(f => f.dimension).filter(Boolean));
  }, [filters]);

  // Get available filters for a specific row (excludes dimensions used by OTHER rows)
  const getAvailableFiltersForRow = (rowIndex: number) => {
    const currentDimension = filters?.[rowIndex]?.dimension;
    return allSupportedFilters.filter(
      filter => filter === currentDimension || !usedDimensions.has(filter)
    );
  };

  // Check if all filter dimensions have been used
  const allFiltersUsed = usedDimensions.size >= allSupportedFilters.length;

  // Handle form field resets when subscription type/source/metric changes
  useFormFieldReset(source, metric, setValue, fields, remove, subscriptionType, metricsData);

  const handleNext = () => {
    onNext();
  };

  const handleAddFilter = () => {
    append({ dimension: "", value: [] });
  };

  return (
    <Stack gap="2xl" direction="column" className="al-p-6">
      <Stack direction="column" gap="lg" className="al-border al-rounded-lg al-p-4">
        {/* Subscription Type */}
        <SubscriptionTypeSelector control={control} isEditMode={isEditMode || isFromTemplate} />

        {/* Subscription Name */}
        <div>
          <Label htmlFor="subscription-name">
            Subscription name <span className="al-text-red-600">*</span>
          </Label>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <>
                <Input
                  id="subscription-name"
                  placeholder="e.g. high cost alert"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  className={cn("al-mt-2", errors.name && "al-border-red-500")}
                />
                {errors.name && (
                  <Typography variant="body" size="xs" className="al-text-red-500 al-mt-1 al-block">
                    {errors.name.message}
                  </Typography>
                )}
              </>
            )}
          />
        </div>

        {/* Entity Details Card */}
        <EntityDetailsCard
          control={control}
          errors={errors}
          setValue={setValue}
          source={source}
          groupedBySource={effectiveGroupedBySource}
          categoryDisplayNameLookup={categoryDisplayNameLookup}
          categoryToSourceLookup={categoryToSourceLookup}
          metricsForCategory={metricsForCategory}
        />

        {/* Add Filters Section */}
        <div>
          <Typography variant="body" weight="medium" className="al-mb-2">
            Add filters
          </Typography>

          {fields.length > 0 && (
            <div className="al-space-y-3 al-mb-4">
              {fields.map((field, index) => (
                <FilterRow
                  key={field.id}
                  control={control}
                  index={index}
                  errors={errors}
                  onRemove={remove}
                  availableFilters={getAvailableFiltersForRow(index)}
                  searchableFilters={searchableFilters}
                  metricName={metric}
                />
              ))}
            </div>
          )}

          {/* Add Filter button */}
          <Button
            type="button"
            variant="outline"
            onClick={handleAddFilter}
            disabled={!source || !metric || allFiltersUsed}
            className="al-text-sm"
          >
            + Add Filter
          </Button>
          {(!source || !metric) && (
            <Typography variant="body" size="xs" className="al-text-gray-500 al-mt-2 al-block">
              Please select a category/source and metric before adding filters
            </Typography>
          )}
          {source && metric && allFiltersUsed && (
            <Typography variant="body" size="xs" className="al-text-gray-500 al-mt-2 al-block">
              All available filter dimensions have been added
            </Typography>
          )}
        </div>

        {/* Next Button */}
        <Stack direction="row" justify="end" className="al-pt-4">
          <Button
            onClick={handleNext}
            disabled={!isFormValid}
            className="al-bg-blue-600 hover:al-bg-blue-700"
          >
            Next
          </Button>
        </Stack>
      </Stack>

      {/* Step 2: Customize - Upcoming */}
      <StepCard title="Customize Your Subscription" status="upcoming" />

      {/* Step 3: Destination - Upcoming */}
      <StepCard title="Select your Destination" status="upcoming" />
    </Stack>
  );
};

export default SubscriptionTypeStep;
